'use client';

import { type Dispatch, type RefObject, type SetStateAction } from 'react';
import { flushSync } from 'react-dom';

import { useSeoRedirect } from '@/features/seo-listing/hooks';
import type { SeoResolveData } from '@/features/seo-listing/types';
import type { FilterAction, FilterState } from '../store';
import {
  applySeoDisabledNavigation,
  buildFilterReloadContext,
  type FilterReloadContext,
  getBrowserPathname,
  hasRestoredListingFiltersChanged,
  type ListingIdentityFilters,
  type ListingQueryFilters,
  listingIdentityFromFilterState,
} from '../utils';

interface UseListingSeoRedirectBridgeParams {
  basePath: '/fanfics' | '/originals';
  filterStateRef: RefObject<FilterState>;
  filterIdentityByPathRef: RefObject<Map<string, ListingIdentityFilters>>;
  dispatch: Dispatch<FilterAction>;
  setSeoPage: Dispatch<SetStateAction<SeoResolveData | null>>;
  setSeoPageTitle: Dispatch<SetStateAction<string | null>>;
  restoreCatalogSeoPage: () => void;
  endSeoNavigation: () => void;
  updateURL: (params: ListingQueryFilters) => void;
  syncRelatedFilterOptions: (context: FilterReloadContext) => void;
}

/**
 * Wires useSeoRedirect to the grid state: restores filters from the resolved
 * permalink, patches SEO chrome, handles SEO-disabled fallback, and resyncs
 * the URL / facet dictionaries once the redirect round-trip settles.
 */
export const useListingSeoRedirectBridge = ({
  basePath,
  filterStateRef,
  filterIdentityByPathRef,
  dispatch,
  setSeoPage,
  setSeoPageTitle,
  restoreCatalogSeoPage,
  endSeoNavigation,
  updateURL,
  syncRelatedFilterOptions,
}: UseListingSeoRedirectBridgeParams) =>
  useSeoRedirect({
    onFiltersRestored: (restoredFilters) => {
      // Compare restored values with the already-current filterState BY CONTENT,
      // not by reference. handleFandomsChange/handleCharactersChange etc. have
      // already synchronously set the needed values in filterState BEFORE the SEO
      // round-trip finished — restoredFilters here usually semantically matches
      // the existing state, but parseListingGetUrl always returns new arrays.
      // Dispatching SYNC_FROM_URL with new array references (even with the same
      // content) makes React see a new filterState, which re-triggers a duplicate
      // fanfics-custom/v2 request through the loadFanfics effect that depends on
      // these arrays by reference. Dispatch only when at least one dimension
      // really differs.
      if (!hasRestoredListingFiltersChanged(restoredFilters, filterStateRef.current)) {
        return;
      }

      flushSync(() => {
        dispatch({
          type: 'SYNC_FROM_URL',
          payload: {
            selectedFandoms: restoredFilters.fandoms,
            selectedTags: restoredFilters.tags,
            selectedUniverses: restoredFilters.universes,
            selectedStatuses: restoredFilters.statuses,
            selectedRatings: restoredFilters.ratings,
            selectedCharacters: restoredFilters.characters,
            selectedPairingCharacters: restoredFilters.pairingCharacters,
          },
        });
      });
    },
    onMetaResolved: (resolved) => {
      setSeoPage(resolved);
      setSeoPageTitle(resolved.title ?? null);
    },
    onSeoDisabled: () => {
      const browserPath = getBrowserPathname();
      applySeoDisabledNavigation({
        basePath,
        currentFilters: filterStateRef.current,
        expectedIdentity: filterIdentityByPathRef.current.get(browserPath) ?? null,
        restoreCatalogSeoPage,
        clearSeoPage: () => {
          setSeoPage(null);
          setSeoPageTitle(null);
        },
      });
    },
    // Runs once the whole redirect+resolve round-trip is finished (success,
    // soft-degraded, not_found, or error) — release the pending guard now.
    onNavigationSettled: (hasRedirected) => {
      endSeoNavigation();
      // The updateURL effect was skipped by the guard while waiting for SEO —
      // sync the address bar manually. On a permalink updateURL keeps the path
      // and appends pass-through query (e.g. ?characters=).
      if (!hasRedirected) {
        const currentFilters = filterStateRef.current;
        updateURL({
          page: currentFilters.currentPage,
          sort: currentFilters.sortBy,
          search: currentFilters.searchQuery,
          fandoms: currentFilters.selectedFandoms,
          tags: currentFilters.selectedTags,
          universes: currentFilters.selectedUniverses,
          statuses: currentFilters.selectedStatuses,
          ratings: currentFilters.selectedRatings,
          characters: currentFilters.selectedCharacters,
          pairingCharacters: currentFilters.selectedPairingCharacters,
        });
      }

      // Always resync facet dictionaries after SEO settles. Cache key no-ops
      // when identity is unchanged (e.g. character SEO 404 on a fandom page).
      // Required for SEO 404/fallback after clearing filters: the identity
      // effect skips while pending, so without this call characters stay empty.
      const currentFilters = filterStateRef.current;
      const browserPath = getBrowserPathname();
      filterIdentityByPathRef.current.set(
        browserPath,
        listingIdentityFromFilterState({
          selectedFandoms: currentFilters.selectedFandoms,
          selectedCharacters: currentFilters.selectedCharacters,
          selectedTags: currentFilters.selectedTags,
          selectedPairingCharacters: currentFilters.selectedPairingCharacters,
        }),
      );
      syncRelatedFilterOptions(
        buildFilterReloadContext(browserPath, {
          selectedFandoms: currentFilters.selectedFandoms,
          selectedCharacters: currentFilters.selectedCharacters,
          selectedTags: currentFilters.selectedTags,
          selectedUniverses: currentFilters.selectedUniverses,
          selectedPairingCharacters: currentFilters.selectedPairingCharacters,
        }),
      );
    },
  });
